import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Users, Eye, DollarSign, CheckCircle, Clock, Trash2,
  ToggleLeft, ToggleRight, ExternalLink, ChevronDown, ChevronUp, Palette, Save, KeyRound, Upload, Plus, X, Loader2, Send
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const t = (lang, de, en) => (lang === 'de' ? de : en);

function getAdminHeaders() {
  const token = localStorage.getItem('adminToken');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

const statusLabels = {
  inquiry: { de: 'Anfrage', en: 'Inquiry', color: 'bg-blue-500/20 text-blue-400', next: 'consultation' },
  consultation: { de: 'Beratung', en: 'Consultation', color: 'bg-yellow-500/20 text-yellow-400', next: 'treatment_done' },
  treatment_done: { de: 'Behandelt', en: 'Treated', color: 'bg-green-500/20 text-green-400', next: 'paid' },
  paid: { de: 'Ausgezahlt', en: 'Paid', color: 'bg-emerald-500/20 text-emerald-400', next: null },
};

const PartnerAdmin = ({ skipAuth = false }) => {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const [authenticated, setAuthenticated] = useState(skipAuth || !!localStorage.getItem('adminToken'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [partners, setPartners] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('partners');
  const [expandedPartner, setExpandedPartner] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [resetPasswordId, setResetPasswordId] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', phone: '', social_media: '', commission: 150, discount_text: '10%', send_welcome_email: true });
  const [creating, setCreating] = useState(false);
  const [sendingCredentials, setSendingCredentials] = useState(null);
  const [credentialPassword, setCredentialPassword] = useState('');

  const headers = getAdminHeaders();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, rRes] = await Promise.all([
        fetch(`${API}/api/admin/partners`, { headers }),
        fetch(`${API}/api/admin/referrals`, { headers }),
      ]);
      if (pRes.ok) setPartners((await pRes.json()).partners || []);
      if (rRes.ok) setReferrals((await rRes.json()).referrals || []);
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (authenticated) fetchData(); }, [authenticated, fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      localStorage.setItem('adminToken', data.token);
      setAuthenticated(true);
    } catch (err) {
      setLoginError(err.message);
    }
  };

  const togglePartnerActive = async (partnerId, currentActive) => {
    try {
      await fetch(`${API}/api/admin/partners/${partnerId}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ is_active: !currentActive }),
      });
      fetchData();
      toast.success(!currentActive ? 'Partner aktiviert' : 'Partner deaktiviert');
    } catch {
      toast.error('Fehler');
    }
  };

  const deletePartner = async (partnerId) => {
    if (!window.confirm(t(language, 'Partner wirklich löschen?', 'Really delete partner?'))) return;
    try {
      await fetch(`${API}/api/admin/partners/${partnerId}`, { method: 'DELETE', headers });
      fetchData();
      toast.success('Partner gelöscht');
    } catch {
      toast.error('Fehler');
    }
  };

  const resetPartnerPassword = async (partnerId) => {
    if (!resetPasswordValue || resetPasswordValue.length < 6) {
      toast.error(t(language, 'Passwort muss mind. 6 Zeichen haben', 'Password must be at least 6 characters'));
      return;
    }
    try {
      const res = await fetch(`${API}/api/admin/partners/${partnerId}/password`, {
        method: 'PUT', headers,
        body: JSON.stringify({ new_password: resetPasswordValue }),
      });
      if (!res.ok) throw new Error();
      setResetPasswordId(null);
      setResetPasswordValue('');
      toast.success(t(language, 'Passwort zurückgesetzt', 'Password reset'));
    } catch {
      toast.error('Fehler');
    }
  };

  const advanceReferralStatus = async (referralId, nextStatus) => {
    if (!nextStatus) return;
    try {
      await fetch(`${API}/api/admin/referrals/${referralId}/status`, {
        method: 'PUT', headers,
        body: JSON.stringify({ status: nextStatus }),
      });
      fetchData();
      toast.success(t(language, 'Status aktualisiert', 'Status updated'));
    } catch {
      toast.error('Fehler');
    }
  };

  const savePartnerEdit = async (partnerId) => {
    try {
      const res = await fetch(`${API}/api/admin/partners/${partnerId}`, {
        method: 'PUT', headers,
        body: JSON.stringify(editForm),
      });
      let data;
      try {
        data = await res.json();
      } catch {
        if (!res.ok) throw new Error(t(language, `Serverfehler (${res.status})`, `Server error (${res.status})`));
        data = {};
      }
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      setEditingPartner(null);
      fetchData();
      // Show updated slug/code if name changed
      if (data.partner?.slug && data.partner?.promo_code) {
        toast.success(t(language,
          `Gespeichert! Neuer Link: /promo/${data.partner.slug} | Code: ${data.partner.promo_code}`,
          `Saved! New link: /promo/${data.partner.slug} | Code: ${data.partner.promo_code}`
        ));
      } else {
        toast.success(t(language, 'Gespeichert', 'Saved'));
      }
    } catch (err) {
      toast.error(err.message || 'Fehler');
    }
  };

  const startEdit = (p) => {
    setEditingPartner(p.id);
    setEditForm({
      name: p.name || '',
      email: p.email || '',
      phone: p.phone || '',
      social_media: p.social_media || '',
      commission: p.commission,
      accent_color: p.accent_color || '#D4930D',
      discount_text: p.discount_text || '10%',
      custom_headline: p.custom_headline || '',
      custom_description: p.custom_description || '',
    });
  };

  const handleAdminLogoUpload = async (partnerId, file) => {
    if (!file) return;
    setUploadingLogo(partnerId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/admin/partners/${partnerId}/logo`, {
        method: 'POST',
        headers: { Authorization: getAdminHeaders().Authorization },
        body: formData,
      });
      if (!res.ok) throw new Error();
      toast.success(t(language, 'Logo hochgeladen', 'Logo uploaded'));
      fetchData();
    } catch {
      toast.error(t(language, 'Upload fehlgeschlagen', 'Upload failed'));
    } finally {
      setUploadingLogo(null);
    }
  };

  const createPartner = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error(t(language, 'Name, E-Mail und Passwort sind erforderlich', 'Name, email and password are required'));
      return;
    }
    if (createForm.password.length < 6) {
      toast.error(t(language, 'Passwort muss mind. 6 Zeichen haben', 'Password must be at least 6 characters'));
      return;
    }
    // Check for duplicate email locally first
    const emailLower = createForm.email.toLowerCase();
    if (partners.some(p => p.email?.toLowerCase() === emailLower)) {
      toast.error(t(language, 'Diese E-Mail-Adresse ist bereits einem Partner zugeordnet', 'This email is already assigned to a partner'));
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/admin/partners`, {
        method: 'POST', headers,
        body: JSON.stringify(createForm),
      });
      let data;
      try {
        data = await res.json();
      } catch {
        if (!res.ok) throw new Error(
          res.status === 400
            ? t(language, 'E-Mail bereits registriert oder ungültige Daten', 'Email already registered or invalid data')
            : t(language, `Serverfehler (${res.status})`, `Server error (${res.status})`)
        );
        throw new Error(t(language, 'Unerwarteter Serverfehler', 'Unexpected server error'));
      }
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      toast.success(t(language, `Partner "${data.name}" angelegt! Slug: ${data.slug}, Code: ${data.promo_code}`, `Partner "${data.name}" created!`));
      setShowCreateForm(false);
      setCreateForm({ name: '', email: '', password: '', phone: '', social_media: '', commission: 150, discount_text: '10%', send_welcome_email: true });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const sendCredentials = async (partnerId) => {
    setSendingCredentials(partnerId);
    try {
      const res = await fetch(`${API}/api/admin/partners/${partnerId}/send-credentials`, {
        method: 'POST', headers,
        body: JSON.stringify({ password: credentialPassword || null }),
      });
      if (!res.ok) throw new Error();
      toast.success(t(language, 'Zugangsdaten per E-Mail gesendet!', 'Credentials sent via email!'));
      setCredentialPassword('');
      if (credentialPassword) fetchData();
    } catch {
      toast.error(t(language, 'Senden fehlgeschlagen', 'Send failed'));
    } finally {
      setSendingCredentials(null);
    }
  };

  // Summary stats
  const totalPartners = partners.length;
  const activePartners = partners.filter(p => p.is_active).length;
  const totalReferrals = referrals.length;
  const totalTreatments = referrals.filter(r => ['treatment_done', 'paid'].includes(r.status)).length;
  const totalPaid = referrals.filter(r => r.status === 'paid').reduce((sum, r) => sum + (r.commission || 0), 0);

  if (!authenticated) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
        <form onSubmit={handleLogin} className={`w-full max-w-sm mx-4 rounded-2xl p-8 border ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
          <h1 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Partner Admin</h1>
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="E-Mail" className={`mb-3 ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`} />
          <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Passwort" data-testid="admin-partner-password" className={`mb-3 ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`} />
          {loginError && <p className="text-red-400 text-sm mb-3">{loginError}</p>}
          <Button type="submit" className="w-full bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white" data-testid="admin-partner-login-btn">Login</Button>
        </form>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
      <header className={`sticky top-0 z-40 backdrop-blur-xl border-b ${isDark ? 'bg-[#0a0a0a]/90 border-white/5' : 'bg-white/90 border-gray-200'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <h1 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Partner Admin</h1>
          <div className="flex gap-2">
            {['partners', 'referrals'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab ? (isDark ? 'bg-[#7BA7C2]/20 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]') : (isDark ? 'text-white/50 hover:text-white' : 'text-gray-500 hover:text-gray-700')}`} data-testid={`admin-tab-${tab}`}>
                {tab === 'partners' ? t(language, 'Partner', 'Partners') : t(language, 'Empfehlungen', 'Referrals')}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8">
          {[
            { label: t(language, 'Partner', 'Partners'), value: totalPartners, sub: `${activePartners} ${t(language, 'aktiv', 'active')}` },
            { label: t(language, 'Anfragen', 'Inquiries'), value: totalReferrals },
            { label: t(language, 'Behandlungen', 'Treatments'), value: totalTreatments },
            { label: t(language, 'Ausgezahlt', 'Paid'), value: `${totalPaid}€` },
            { label: t(language, 'Ausstehend', 'Pending'), value: `${referrals.filter(r => r.status === 'treatment_done').reduce((s, r) => s + (r.commission || 0), 0)}€` },
          ].map((s, i) => (
            <div key={i} className={`rounded-xl p-4 border ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
              <div className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{s.value}</div>
              <div className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{s.label}</div>
              {s.sub && <div className={`text-xs mt-1 ${isDark ? 'text-white/25' : 'text-gray-300'}`}>{s.sub}</div>}
            </div>
          ))}
        </div>

        {loading && <div className="text-center py-8"><svg className={`animate-spin w-8 h-8 mx-auto ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="60 30" /></svg></div>}

        {/* Partners Tab */}
        {activeTab === 'partners' && !loading && (
          <div className="space-y-4">
            {/* Create Partner Button + Form */}
            <div className="flex justify-end mb-2">
              <Button onClick={() => setShowCreateForm(!showCreateForm)}
                className={showCreateForm ? `text-sm ${isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-700'}` : 'bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white text-sm'}
                data-testid="create-partner-btn">
                {showCreateForm ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                {showCreateForm ? t(language, 'Abbrechen', 'Cancel') : t(language, 'Neuer Partner', 'New Partner')}
              </Button>
            </div>

            {showCreateForm && (
              <div className={`rounded-2xl p-5 border ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
                <h3 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t(language, 'Neuen Partner anlegen', 'Create New Partner')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Name *', 'Name *')}</label>
                    <Input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} placeholder="z.B. Max Influencer"
                      className={isDark ? 'bg-white/5 border-white/10 text-white' : ''} data-testid="create-partner-name" />
                  </div>
                  <div>
                    <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'E-Mail *', 'Email *')}</label>
                    <Input type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} placeholder="partner@example.de"
                      className={isDark ? 'bg-white/5 border-white/10 text-white' : ''} data-testid="create-partner-email" />
                  </div>
                  <div>
                    <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Passwort * (mind. 6 Zeichen)', 'Password * (min. 6 chars)')}</label>
                    <Input type="text" value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} placeholder="Starkes Passwort"
                      className={isDark ? 'bg-white/5 border-white/10 text-white' : ''} data-testid="create-partner-password" />
                  </div>
                  <div>
                    <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Telefon', 'Phone')}</label>
                    <Input value={createForm.phone} onChange={e => setCreateForm(f => ({ ...f, phone: e.target.value }))} placeholder="+49..."
                      className={isDark ? 'bg-white/5 border-white/10 text-white' : ''} />
                  </div>
                  <div>
                    <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Provision (€)', 'Commission (€)')}</label>
                    <Input type="number" value={createForm.commission} onChange={e => setCreateForm(f => ({ ...f, commission: parseFloat(e.target.value) || 150 }))}
                      className={isDark ? 'bg-white/5 border-white/10 text-white' : ''} />
                  </div>
                  <div>
                    <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Rabatt-Text', 'Discount text')}</label>
                    <Input value={createForm.discount_text} onChange={e => setCreateForm(f => ({ ...f, discount_text: e.target.value }))}
                      className={isDark ? 'bg-white/5 border-white/10 text-white' : ''} />
                  </div>
                </div>
                <label className={`flex items-center gap-2 mb-4 cursor-pointer`}>
                  <input type="checkbox" checked={createForm.send_welcome_email} onChange={e => setCreateForm(f => ({ ...f, send_welcome_email: e.target.checked }))}
                    className="rounded" data-testid="create-partner-send-email" />
                  <span className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                    {t(language, 'Willkommens-E-Mail mit Zugangsdaten senden', 'Send welcome email with credentials')}
                  </span>
                </label>
                <Button onClick={createPartner} disabled={creating} className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white text-sm" data-testid="submit-create-partner">
                  {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                  {creating ? t(language, 'Wird angelegt...', 'Creating...') : t(language, 'Partner anlegen', 'Create Partner')}
                </Button>
              </div>
            )}

            {partners.map(p => (
              <div key={p.id} className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
                <div className="px-5 py-4 flex items-center justify-between cursor-pointer" onClick={() => setExpandedPartner(expandedPartner === p.id ? null : p.id)}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${p.is_active ? 'bg-green-400' : 'bg-red-400'}`} />
                    <div className="min-w-0">
                      <div className={`font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{p.name}</div>
                      <div className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{p.promo_code} &middot; /promo/{p.slug}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className={`hidden sm:flex items-center gap-4 text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                      <span><Eye className="w-3 h-3 inline mr-1" />{p.stats?.clicks || 0}</span>
                      <span><Users className="w-3 h-3 inline mr-1" />{p.stats?.inquiries || 0}</span>
                      <span><CheckCircle className="w-3 h-3 inline mr-1" />{p.stats?.treatments || 0}</span>
                    </div>
                    {expandedPartner === p.id ? <ChevronUp className={`w-4 h-4 ${isDark ? 'text-white/40' : 'text-gray-400'}`} /> : <ChevronDown className={`w-4 h-4 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />}
                  </div>
                </div>

                {expandedPartner === p.id && (
                  <div className={`px-5 pb-5 pt-2 border-t ${isDark ? 'border-white/5' : 'border-gray-50'}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                        <p><strong>E-Mail:</strong> {p.email}</p>
                        <p><strong>Telefon:</strong> {p.phone || '-'}</p>
                        <p><strong>Social:</strong> {p.social_media || '-'}</p>
                        <p><strong>Provision:</strong> {p.commission}€</p>
                        <p><strong>Rabatt:</strong> {p.discount_text || '10%'}</p>
                        <p><strong>Registriert:</strong> {new Date(p.created_at).toLocaleDateString('de-DE')}</p>
                      </div>
                      <div>
                        <a href={`/promo/${p.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-[#7BA7C2] hover:underline mb-3">
                          <ExternalLink className="w-4 h-4" /> {t(language, 'Promo-Seite öffnen', 'Open promo page')}
                        </a>
                      </div>
                    </div>

                    {/* Edit form */}
                    {editingPartner === p.id ? (
                      <div className={`p-4 rounded-xl mb-4 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                        {/* Profile fields */}
                        <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{t(language, 'Profil', 'Profile')}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                          <div>
                            <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Name', 'Name')}</label>
                            <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className={`${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`} />
                          </div>
                          <div>
                            <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'E-Mail', 'Email')}</label>
                            <Input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} className={`${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`} />
                          </div>
                          <div>
                            <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Telefon', 'Phone')}</label>
                            <Input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} className={`${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`} />
                          </div>
                          <div>
                            <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Social Media', 'Social Media')}</label>
                            <Input value={editForm.social_media} onChange={e => setEditForm(f => ({ ...f, social_media: e.target.value }))} className={`${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`} />
                          </div>
                        </div>

                        {/* Logo Upload */}
                        <div className="mb-4">
                          <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Partner-Logo', 'Partner Logo')}</label>
                          <div className="flex items-center gap-3">
                            {p.logo_url && <img src={p.logo_url} alt="Logo" className="h-12 w-12 object-contain rounded-lg border border-white/10" />}
                            <label className={`cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${isDark ? 'bg-white/10 hover:bg-white/15 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>
                              <Upload className="w-3.5 h-3.5" />
                              {uploadingLogo === p.id ? t(language, 'Lädt...', 'Uploading...') : t(language, 'Logo hochladen', 'Upload Logo')}
                              <input type="file" accept="image/*" onChange={e => handleAdminLogoUpload(p.id, e.target.files?.[0])} className="hidden" />
                            </label>
                          </div>
                        </div>

                        {/* Promo settings */}
                        <div className={`text-xs font-semibold uppercase tracking-wider mb-2 mt-4 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{t(language, 'Promo-Einstellungen', 'Promo Settings')}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                          <div>
                            <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Provision (€)', 'Commission (€)')}</label>
                            <Input type="number" value={editForm.commission} onChange={e => setEditForm(f => ({ ...f, commission: parseFloat(e.target.value) }))} className={`${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`} />
                          </div>
                          <div>
                            <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Rabatt-Text', 'Discount text')}</label>
                            <Input value={editForm.discount_text} onChange={e => setEditForm(f => ({ ...f, discount_text: e.target.value }))} className={`${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`} />
                          </div>
                          <div>
                            <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Akzentfarbe', 'Accent color')}</label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={editForm.accent_color} onChange={e => setEditForm(f => ({ ...f, accent_color: e.target.value }))} className="w-10 h-10 rounded-lg cursor-pointer border-0" />
                              <Input value={editForm.accent_color} onChange={e => setEditForm(f => ({ ...f, accent_color: e.target.value }))} className={`flex-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`} />
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Eigene Headline (optional)', 'Custom headline (optional)')}</label>
                            <Input value={editForm.custom_headline} onChange={e => setEditForm(f => ({ ...f, custom_headline: e.target.value }))} className={`${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`} />
                          </div>
                          <div>
                            <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Eigene Beschreibung (optional)', 'Custom description (optional)')}</label>
                            <Input value={editForm.custom_description} onChange={e => setEditForm(f => ({ ...f, custom_description: e.target.value }))} className={`${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`} />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={() => savePartnerEdit(p.id)} className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white text-sm"><Save className="w-4 h-4 mr-1" /> {t(language, 'Speichern', 'Save')}</Button>
                          <Button variant="outline" onClick={() => setEditingPartner(null)} className={`text-sm ${isDark ? 'text-white border-white/20' : ''}`}>{t(language, 'Abbrechen', 'Cancel')}</Button>
                        </div>
                      </div>
                    ) : null}

                    <div className="flex gap-2 flex-wrap">
                      <Button variant="outline" onClick={() => startEdit(p)} className={`text-sm ${isDark ? 'text-white border-white/20' : ''}`} data-testid={`edit-partner-${p.id}`}>
                        <Palette className="w-4 h-4 mr-1" /> {t(language, 'Bearbeiten', 'Edit')}
                      </Button>
                      <Button variant="outline" onClick={() => togglePartnerActive(p.id, p.is_active)} className={`text-sm ${isDark ? 'text-white border-white/20' : ''}`}>
                        {p.is_active ? <><ToggleRight className="w-4 h-4 mr-1" /> {t(language, 'Deaktivieren', 'Deactivate')}</> : <><ToggleLeft className="w-4 h-4 mr-1" /> {t(language, 'Aktivieren', 'Activate')}</>}
                      </Button>
                      <Button variant="outline" onClick={() => deletePartner(p.id)} className="text-sm text-red-400 border-red-400/30 hover:bg-red-400/10">
                        <Trash2 className="w-4 h-4 mr-1" /> {t(language, 'Löschen', 'Delete')}
                      </Button>
                      <Button variant="outline" onClick={() => { setResetPasswordId(resetPasswordId === p.id ? null : p.id); setResetPasswordValue(''); }} className={`text-sm ${isDark ? 'text-white border-white/20' : ''}`}>
                        <KeyRound className="w-4 h-4 mr-1" /> {t(language, 'Passwort', 'Password')}
                      </Button>
                    </div>

                    {resetPasswordId === p.id && (
                      <div className={`mt-3 p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                        <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Neues Passwort setzen', 'Set new password')}</label>
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            value={resetPasswordValue}
                            onChange={e => setResetPasswordValue(e.target.value)}
                            placeholder={t(language, 'Neues Passwort (mind. 6 Zeichen)', 'New password (min. 6 chars)')}
                            className={`flex-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`}
                            data-testid={`reset-password-input-${p.id}`}
                          />
                          <Button onClick={() => resetPartnerPassword(p.id)} className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white text-sm" data-testid={`reset-password-btn-${p.id}`}>
                            <Save className="w-4 h-4 mr-1" /> {t(language, 'Setzen', 'Set')}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Send credentials */}
                    <div className={`mt-3 p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                      <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                        {t(language, 'Zugangsdaten per E-Mail senden (Login, Promo-Link, Dashboard)', 'Send credentials via email')}
                      </label>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          value={sendingCredentials === p.id ? credentialPassword : ''}
                          onChange={e => { setSendingCredentials(p.id); setCredentialPassword(e.target.value); }}
                          placeholder={t(language, 'Neues Passwort (leer = nur Infos ohne PW)', 'New password (empty = info only)')}
                          className={`flex-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`}
                          data-testid={`credential-password-${p.id}`}
                        />
                        <Button
                          onClick={() => sendCredentials(p.id)}
                          disabled={sendingCredentials === p.id && !credentialPassword && false}
                          className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white text-sm"
                          data-testid={`send-credentials-btn-${p.id}`}
                        >
                          <Send className="w-4 h-4 mr-1" /> {t(language, 'Senden', 'Send')}
                        </Button>
                      </div>
                      <p className={`text-xs mt-1.5 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                        {t(language, 'Tipp: Passwort eingeben = wird gesetzt + gesendet. Leer lassen = nur Links senden.', 'Tip: Enter password = set + send. Leave empty = send links only.')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {partners.length === 0 && !loading && (
              <div className={`text-center py-12 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                {t(language, 'Noch keine Partner registriert.', 'No partners registered yet.')}
              </div>
            )}
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && !loading && (
          <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
            {referrals.length === 0 ? (
              <div className={`text-center py-12 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                {t(language, 'Noch keine Empfehlungen.', 'No referrals yet.')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className={isDark ? 'text-white/40' : 'text-gray-400'}>
                      <th className="px-4 py-3 font-medium">Partner</th>
                      <th className="px-4 py-3 font-medium">{t(language, 'Kunde', 'Client')}</th>
                      <th className="px-4 py-3 font-medium hidden sm:table-cell">E-Mail</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium hidden sm:table-cell">{t(language, 'Provision', 'Commission')}</th>
                      <th className="px-4 py-3 font-medium">{t(language, 'Aktion', 'Action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map(r => {
                      const st = statusLabels[r.status] || statusLabels.inquiry;
                      return (
                        <tr key={r.id} className={`border-t ${isDark ? 'border-white/5' : 'border-gray-50'}`}>
                          <td className={`px-4 py-3 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{r.partner_name}</td>
                          <td className={`px-4 py-3 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{r.client_name}</td>
                          <td className={`px-4 py-3 hidden sm:table-cell ${isDark ? 'text-white/50' : 'text-gray-400'}`}>{r.client_email}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${st.color}`}>
                              {language === 'de' ? st.de : st.en}
                            </span>
                          </td>
                          <td className={`px-4 py-3 hidden sm:table-cell font-medium ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{r.commission}€</td>
                          <td className="px-4 py-3">
                            {st.next ? (
                              <button onClick={() => advanceReferralStatus(r.id, st.next)} className="text-xs px-3 py-1.5 rounded-lg bg-[#7BA7C2]/20 text-[#7BA7C2] hover:bg-[#7BA7C2]/30 transition-colors" data-testid={`advance-referral-${r.id}`}>
                                &rarr; {language === 'de' ? statusLabels[st.next].de : statusLabels[st.next].en}
                              </button>
                            ) : (
                              <span className="text-xs text-emerald-400">&#10003;</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerAdmin;
